import React, { ReactElement } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { FormField } from '../../../../global/form/form-field/form-field';
import { SingleSelectInput } from '../../../../global/single-select/single-select-input';
import OECheckbox from '../../../../global/oe-checkbox/oe-checkbox';
import { NUMBERS_ONLY_REG_EXP, ONLY_SPACES_REG_EXP } from '../../../../../constants/regex.constants';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { OEToggle } from '../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../global/text/text';
import { RichTextEditor } from '../../../../global/rich-text-editor';
import { AttendeeAttendanceMethod, AttendeeRegistrationStatus, PodiumQuestionAnswerPriorityType } from '../../../../../api/attendees.dao';
import { UploadImageS3Presentation } from '../../shared/upload-image/upload-image-to-s3.presentation';
import { AttendeeFormViewModel } from './attendee-form.viewmodel';
import { AttendeeFormDataViewModel } from './attendee-form-data.viewmodel';
import { attendanceMethods, attendeeQAPriorityOptions } from '../../../../../constants/attendee-values';
import {
    ACCESS_CODE_TEXT,
    ASSISTANT_EMAIL_TEXT,
    ATTENDANCE_METHOD_TEXT,
    BIOGRAPHY_TEXT,
    EMAIL_NOT_PROVIDED_TEXT,
    EMAIL_TEXT,
    EXTERNAL_ATTENDEE_ID_TEXT,
    FIRST_NAME_TEXT,
    LAST_NAME_TEXT,
    ATTENDEE_MAPPED_TEXT,
    ATTENDEE_NOT_MAPPED_TEXT,
    MANAGE_MAPPING_TEXT,
    NO_ATTENDANCE_TYPE_SELECTED_TEXT,
    NO_MATCHING_SEARCH_CRITERIA_TEXT,
    NO_ORGANIZATIONS_TEXT,
    ORGANIZATION_TEXT,
    PHONE_NUMBER_TEXT,
    PODIUM_QA_PRIORITY_EXPLANATION_TEXT,
    PODIUM_QA_PRIORITY_TEXT,
    PRIORITY_IN_ORGANIZATION_TEXT,
    PROFILE_IMAGE_TEXT,
    QA_NOTES_TEXT,
    REGISTRATION_STATUS_TEXT,
    SALUTATION_TEXT,
    TITLE_TEXT
} from '../attendees.texts';
import { DIMENSIONS_PROFILE_IMAGE } from '../../../../../constants/file.constants';
import DefaultButton from '../../../../global/default-button/default-button';

interface IProps {
    formData: AttendeeFormDataViewModel;
    formVM: AttendeeFormViewModel;
    intl: IntlShape;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    AddNewOrganizationButton: () => ReactElement;
}

const getMappingStatusText = (contactId: number | null) =>
    contactId != null ? ATTENDEE_MAPPED_TEXT : ATTENDEE_NOT_MAPPED_TEXT;

const AttendeeFormBodyPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { formData, formVM, AddNewOrganizationButton, intl } = props;
    const removeOnlySpacesFromField = mobx.action((value: string, key: string) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            formData[key] = '';
        }
    });
    const showErrorMessage = formVM.showErrorMessages();

    return (
        <form className='oe-form'>
            <div className='oe-form__body'>
                <FormField label={<FormattedMessage {...ORGANIZATION_TEXT} />}>
                    <SingleSelectInput
                        className='oe-form-field__control'
                        keyProperty='organizationId'
                        nameProperty='name'
                        value={formData.organizationId}
                        items={formData.getOrganizations()}
                        containerClassName='org-select-popover'
                        ActionButton={AddNewOrganizationButton}
                        onChange={mobx.action(newValue => {
                            formData.organizationId = newValue;
                            formData.priorityInOrganization = null;
                        })}
                        emptyListText={<FormattedMessage {...NO_ORGANIZATIONS_TEXT} />}
                        emptyFilterText={<FormattedMessage {...NO_MATCHING_SEARCH_CRITERIA_TEXT} />}
                    />
                    {showErrorMessage &&
                        <span className='oe-form-field__error'>
                            {formData.getOrganizationError()}
                        </span>}
                </FormField>
                <div className='flex'>
                    <FormField
                        className='m-right-8px'
                        label={<FormattedMessage {...FIRST_NAME_TEXT} />}
                    >
                        <input
                            className='oe-form-field__control'
                            value={formData.firstName || ''}
                            onChange={mobx.action(({ target: { value } }) => {
                                formData.firstName = value;
                            })}
                            onBlur={mobx.action(({ target: { value } }) => {
                                removeOnlySpacesFromField(value, 'firstName');
                            })}
                            autoFocus={!formVM.getAttendeeId()}
                        />
                        {showErrorMessage &&
                            <span className='oe-form-field__error'>{formData.getFirstNameError()}</span>}
                    </FormField>
                    <FormField label={<FormattedMessage {...LAST_NAME_TEXT} />}>
                        <input
                            className='oe-form-field__control'
                            value={formData.lastName || ''}
                            onChange={mobx.action(({ target: { value } }) => {
                                formData.lastName = value;
                            })}
                            onBlur={mobx.action(({ target: { value } }) => {
                                removeOnlySpacesFromField(value, 'lastName');
                            })}
                        />
                        {showErrorMessage &&
                            <span className='oe-form-field__error'>{formData.getLastNameError()}</span>}
                    </FormField>
                </div>
                <FormField
                    label={<FormattedMessage {...EMAIL_TEXT} />}>
                    <div className='flex'>
                        <input
                            className='oe-form-field__control'
                            value={formData.email || ''}
                            onChange={mobx.action(({ target: { value } }) => {
                                formVM.refreshEmailError();
                                formData.email = value;
                            })}
                            onBlur={mobx.action(({ target: { value } }) => {
                                removeOnlySpacesFromField(value, 'email');
                                formData.isMailTouched = true;
                            })}
                        />
                        <OECheckbox
                            label={<FormattedMessage {...EMAIL_NOT_PROVIDED_TEXT} />}
                            checked={formData.emailNotProvided}
                            onChange={formVM.handleEmailsNotProvidedCheckbox}
                            classNames='oe-form-field__checkbox attendee-form__checkbox'
                        />
                    </div>
                    {formVM.shouldShowEmailValidationError() &&
                        <span className='oe-form-field__error'>
                            {formData.getEmailError(formData.email, true) || formVM.getExistingEmailError()}
                        </span>}
                </FormField>
                <FormField label={<FormattedMessage {...ASSISTANT_EMAIL_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.assistantEmail}
                        onChange={mobx.action(({ target: { value } }) => formData.assistantEmail = value)}
                        onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'assistantEmail'))}
                    />
                    { formVM.shouldShowEmailValidationError() &&
                        <span className='oe-form-field__error'>
                            {formData.getEmailError(formData.assistantEmail)}
                        </span>
                    }
                </FormField>
                <FormField label={<FormattedMessage {...TITLE_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.title || ''}
                        onChange={mobx.action(({ target: { value } }) => {
                            formData.title = value;
                        })}
                        onBlur={mobx.action(({ target: { value } }) => {
                            removeOnlySpacesFromField(value, 'title');
                        })}
                    />
                    {showErrorMessage &&
                        <span className='oe-form-field__error'>{formData.getTitleError()}</span>}
                </FormField>
                <FormField
                    label={<FormattedMessage {...SALUTATION_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.salutation || ''}
                        onChange={mobx.action(({ target: { value } }) => {
                            formData.salutation = value;
                        })}
                        onBlur={mobx.action(({ target: { value } }) => {
                            removeOnlySpacesFromField(value, 'salutation');
                        })}
                    />
                    {showErrorMessage &&
                        <span className='oe-form-field__error'>{formData.getSalutationError()}</span>}
                </FormField>
                <FormField
                    label={<FormattedMessage {...PHONE_NUMBER_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.phoneNumber || ''}
                        onChange={mobx.action(({ target: { value } }) => {
                            formData.phoneNumber = value;
                        })}
                        onBlur={mobx.action(({ target: { value } }) => {
                            removeOnlySpacesFromField(value, 'phoneNumber');
                        })}
                    />
                    {showErrorMessage &&
                        <span className='oe-form-field__error'>{formData.getPhoneNumberError()}</span>}
                </FormField>
                <FormField
                    label={<FormattedMessage {...ACCESS_CODE_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.accessCode || ''}
                        onChange={!hasPermission(Permission.ACCESS_CODES_MANAGEMENT) ||
                            formData.registrationStatus !== AttendeeRegistrationStatus.APPROVED ?
                            undefined :
                            mobx.action(({ target: { value } }) => {
                                formData.accessCode = value;
                        })}
                        onBlur={mobx.action(({ target: { value } }) => {
                            removeOnlySpacesFromField(value, 'accessCode');
                        })}
                        disabled={!hasPermission(Permission.ACCESS_CODES_MANAGEMENT) ||
                            formData.registrationStatus !== AttendeeRegistrationStatus.APPROVED
                        }
                    />
                    {showErrorMessage &&
                        <span className='oe-form-field__error'>
                            {formData.getAccessCodeError()}
                        </span>}
                </FormField>
                <FormField
                    label={<FormattedMessage {...REGISTRATION_STATUS_TEXT} />}
                    className='w-50'
                >
                    <select
                        value={formData.registrationStatus}
                        onChange={mobx.action(e => formData.registrationStatus = e.target.value as AttendeeRegistrationStatus)}
                        className='oe-form-field__control'
                    >
                        {Object.keys(AttendeeRegistrationStatus).map((item: AttendeeRegistrationStatus) =>
                        <option
                            key={item}
                            value={AttendeeRegistrationStatus[item]}
                        >
                            {intl.formatMessage({
                                id: `oe.registrationStatus.${item}`
                            })}
                        </option>
                        )}
                    </select>

                </FormField>
                {
                    formData.isAttendeeNewRegistrationStatus() && <div className='oe-form-field'>
                    <div className='row center font-size-12 send-registration-email-toggle'>
                        <OEToggle
                            isSelected={formData.sendRegistrationEmail}
                            onClick={formData.toggleSendRegistrationEmail}
                        />
                        <Text
                            id='oe.attendee.sendRegistrationEmail'
                            message={'Send Registration Email?'}
                        />
                    </div>
                    <div className='send-registration-email-toggle-hint'>
                        {
                            formData.sendRegistrationEmail ? <Text
                                message='Email will be sent to the Attendee when you click Apply.'
                                id='oe.attendee.emailWillBeSentToAttendee'
                                description='Label text'
                            /> : <Text
                                message='Registration status will be updated when you click Apply, but no email will be sent.'
                                id='oe.attendee.registrationStatusWillBeUpdated'
                                description='Label text'
                            />
                        }
                    </div>
                </div>}
                <FormField
                    label={<FormattedMessage {...ATTENDANCE_METHOD_TEXT} />}
                    className='w-50'
                >
                    <select
                        className='oe-form-field__control'
                        value={formData.attendanceMethod}
                        onChange={mobx.action(e => formData.attendanceMethod = e.target.value as AttendeeAttendanceMethod)}
                    >
                        <option value={''}>
                            {intl.formatMessage({...NO_ATTENDANCE_TYPE_SELECTED_TEXT})}
                        </option>
                        {
                            attendanceMethods(intl).map(({ label, value }, i) => {
                                return <option value={value} key={i}>{label}</option>;
                            })
                        }
                    </select>
                </FormField>
                <FormField label={<FormattedMessage {...PRIORITY_IN_ORGANIZATION_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.priorityInOrganization || ''}
                        onChange={mobx.action(({ target: { value } }) => {
                            if (value === '' || NUMBERS_ONLY_REG_EXP.test(value)) {
                                formData.priorityInOrganization = +value;
                            }
                        })}
                    />
                    {showErrorMessage &&
                        <span className='oe-form-field__error'>
                            {formData.getPriorityInOrganizationError()}
                        </span>}
                </FormField>
                <FormField
                    infoIconText={intl.formatMessage(PODIUM_QA_PRIORITY_EXPLANATION_TEXT)}
                    label={intl.formatMessage(PODIUM_QA_PRIORITY_TEXT)}
                    className='w-50'
                >
                    <select
                        className='oe-form-field__control'
                        value={formData.getPodiumQAPriorityType()}
                        onChange={e => formData.setPodiumQAPriorityType(e.target.value as PodiumQuestionAnswerPriorityType | null)}
                    >
                        {
                            attendeeQAPriorityOptions(intl).map(({ label, value }, i) => {
                                return <option value={value} key={i}>{label}</option>;
                            })
                        }
                    </select>
                </FormField>
                {formVM.getAttendeeId() &&
                    <FormField
                        label={<FormattedMessage {...QA_NOTES_TEXT} />}
                    >
                        <input
                            className='oe-form-field__control'
                            value={formData.podiumQaNote}
                            onChange={mobx.action(({ target: { value } }) => formData.podiumQaNote = value)}
                            onBlur={({ target: { value } }) => removeOnlySpacesFromField(value, 'podiumQaNote')}
                        />
                        { showErrorMessage &&
                            <span className='oe-form-field__error'>
                                {formData.getPodiumQaNoteError()}
                            </span>
                        }
                    </FormField>
                }
                <FormField label={<FormattedMessage {...EXTERNAL_ATTENDEE_ID_TEXT} />}>
                    <input
                        className='oe-form-field__control'
                        value={formData.externalAttendeeId || ''}
                        onChange={mobx.action(({ target: { value } }) => {
                            formVM.refreshExternalAttendeeIdError();
                            formData.externalAttendeeId = value;
                        })}
                        onBlur={mobx.action(({ target: { value } }) => {
                            removeOnlySpacesFromField(value, 'externalAttendeeId');
                        })}
                    />
                    {formVM.showErrorMessages &&
                        <span className='oe-form-field__error'>
                            {formData.getExternalAttendeeIDError() || formVM.getExistingExternalAttendeeIdError()}
                        </span>}
                </FormField>
                <div className='oe-form-field row center font-size-12'>
                    <OEToggle
                        isSelected={formData.speaker}
                        onClick={formData.toggleSpeakerValue}
                    />
                    <Text
                        id='oe.attendee.isSpeaker'
                        message={'Attendee is a Speaker'}
                    />
                </div>

                <UploadImageS3Presentation
                        vm={formData.uploadProfileImageS3VM}
                        minDimensions={DIMENSIONS_PROFILE_IMAGE}
                        fieldName={intl.formatMessage(PROFILE_IMAGE_TEXT)}
                />
                <FormField label={<FormattedMessage {...BIOGRAPHY_TEXT} />}>
                    <RichTextEditor
                        className='m-top-0px'
                        id='template-body'
                        value={formData.biography}
                        onChange={mobx.action((event, editor) => {
                            formData.setBiography(editor.getData());
                        })}
                        validationError={formData.getBiographyError(intl)}
                    />
                </FormField>
                {formVM.getAttendeeId() &&
                    <div className='oe-form-field'>
                        <span className='attendee-form__mapping-status'>
                            <FormattedMessage {...getMappingStatusText(formVM.getContactId())} />
                        </span>
                        {hasPermission(Permission.MANAGE_ATTENDEE_ROLODEX_MAPPING) &&
                            <DefaultButton
                                text={<FormattedMessage {...MANAGE_MAPPING_TEXT} />}
                                modifier='fat'
                                onClick={formVM.openManageMappingPopup}
                                className='attendee-form__manage-mapping-button'
                            />
                        }
                    </div>
                }
            </div>
        </form>
    );
}));

export { AttendeeFormBodyPresentation };
